import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, ChevronRight, Plus, X, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_ADDR = { nome: '', via: '', citta: '', cap: '', provincia: '', paese: 'Italia', predefinito: false };

export default function Account() {
  const { user, token, loading, login, register, logout, refresh } = useAuth();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ nome: '', email: '', password: '' });
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  const [editProfile, setEditProfile] = useState(false);
  const [profile, setProfile] = useState({ nome: '', email: '' });
  const [addrOpen, setAddrOpen] = useState(false);
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setOrders(r.data)).catch(() => {});
  }, [token]);

  useEffect(() => { if (user) setProfile({ nome: user.nome, email: user.email }); }, [user]);

  if (loading) return <main className="container-essenya py-32 text-center text-essenya-text">Caricamento...</main>;

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (mode === 'login') await login(form.email, form.password);
      else await register(form.nome, form.email, form.password);
      toast.success('Benvenuto in ESSENYA');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const saveProfile = async () => {
    try {
      await axios.put(`${API}/auth/me`, { nome: profile.nome, email: profile.email }, auth);
      await refresh();
      setEditProfile(false);
      toast.success('Profilo aggiornato');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const openAddrForm = (a = null) => {
    if (a) { setAddr({ ...a }); setEditingId(a.id); }
    else { setAddr(EMPTY_ADDR); setEditingId(null); }
    setAddrOpen(true);
  };

  const saveAddr = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await axios.put(`${API}/auth/addresses/${editingId}`, addr, auth);
      else await axios.post(`${API}/auth/addresses`, addr, auth);
      await refresh();
      setAddrOpen(false);
      toast.success('Indirizzo salvato');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const deleteAddr = async (id) => {
    try { await axios.delete(`${API}/auth/addresses/${id}`, auth); await refresh(); toast.success('Indirizzo eliminato'); }
    catch { toast.error('Errore'); }
  };

  if (!user) {
    return (
      <main data-testid="account-auth" className="container-essenya py-20 max-w-md">
        <h1 className="font-display text-4xl mb-3">{mode === 'login' ? 'Accedi' : 'Registrati'}</h1>
        <p className="text-essenya-text mb-8">{mode === 'login' ? 'Accedi al tuo account ESSENYA.' : 'Crea un account per gestire i tuoi ordini.'}</p>
        <form onSubmit={submit} className="space-y-4">
          {mode === 'register' && (
            <input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} required placeholder="Nome completo" data-testid="auth-nome" className="w-full bg-white border border-essenya-border rounded-lg px-4 py-3 outline-none focus:border-essenya-ink" />
          )}
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="Email" data-testid="auth-email" className="w-full bg-white border border-essenya-border rounded-lg px-4 py-3 outline-none focus:border-essenya-ink" />
          <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required placeholder="Password" minLength={6} data-testid="auth-password" className="w-full bg-white border border-essenya-border rounded-lg px-4 py-3 outline-none focus:border-essenya-ink" />
          <button type="submit" data-testid="auth-submit" className="btn-primary w-full">{mode === 'login' ? 'Accedi' : 'Crea account'}</button>
        </form>
        <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} data-testid="auth-switch" className="mt-6 text-sm underline">
          {mode === 'login' ? 'Non hai un account? Registrati' : 'Hai già un account? Accedi'}
        </button>
      </main>
    );
  }

  const addresses = user.addresses || [];

  return (
    <main data-testid="account-dashboard" className="container-essenya py-16 max-w-2xl">
      {/* Profile */}
      <div className="flex items-start justify-between mb-4">
        <h1 className="font-display text-3xl md:text-4xl" data-testid="account-name">{user.nome}</h1>
        <button onClick={() => setEditProfile(v => !v)} data-testid="profile-edit-toggle" className="liquid-glass-button rounded-2xl px-5 py-2.5 text-sm font-medium">{editProfile ? 'Annulla' : 'Modifica'}</button>
      </div>

      {editProfile ? (
        <div className="card-essenya p-6 mb-10 space-y-4" data-testid="profile-edit-form">
          <div>
            <label className="text-xs text-essenya-text">Nome</label>
            <input value={profile.nome} onChange={e => setProfile({ ...profile, nome: e.target.value })} data-testid="profile-nome-input" className="w-full bg-white border border-essenya-border rounded-xl px-4 py-3 mt-1 outline-none focus:border-essenya-ink" />
          </div>
          <div>
            <label className="text-xs text-essenya-text">Email</label>
            <input type="email" value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })} data-testid="profile-email-input" className="w-full bg-white border border-essenya-border rounded-xl px-4 py-3 mt-1 outline-none focus:border-essenya-ink" />
          </div>
          <button onClick={saveProfile} data-testid="profile-save" className="btn-primary w-full">Salva</button>
        </div>
      ) : (
        <div className="card-essenya p-6 mb-10" data-testid="profile-email-row">
          <div className="flex items-center justify-between">
            <span className="text-essenya-text">Email</span>
            <span className="font-medium">{user.email}</span>
          </div>
        </div>
      )}

      {/* Addresses */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl md:text-3xl">Indirizzi</h2>
        <button onClick={() => openAddrForm()} data-testid="address-add" className="liquid-glass-button rounded-2xl px-5 py-2.5 text-sm font-medium inline-flex items-center gap-1.5"><Plus size={16} strokeWidth={2} /> Aggiungi</button>
      </div>

      {addresses.length === 0 && !addrOpen && (
        <div className="card-essenya p-8 text-center text-essenya-text mb-10" data-testid="address-empty">Nessun indirizzo salvato.</div>
      )}

      <div className="space-y-3 mb-10" data-testid="address-list">
        {addresses.map(a => (
          <div key={a.id} className="card-essenya p-5 flex items-center gap-4" data-testid={`address-${a.id}`}>
            <div className="w-11 h-11 rounded-xl bg-essenya-surface2 flex items-center justify-center flex-shrink-0"><MapPin size={20} strokeWidth={1.5} /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium">{a.nome}</p>
                {a.predefinito && <span className="text-[10px] bg-essenya-surface2 text-essenya-ink px-2 py-0.5 rounded-md uppercase tracking-wide" data-testid={`address-default-${a.id}`}>Predefinito</span>}
              </div>
              <p className="text-essenya-text text-sm truncate">{a.via}, {a.cap} {a.citta}{a.provincia ? ` ${a.provincia}` : ''}, {a.paese}</p>
            </div>
            <button onClick={() => deleteAddr(a.id)} data-testid={`address-delete-${a.id}`} className="p-2 text-essenya-text hover:text-red-500 transition-colors" aria-label="Elimina"><Trash2 size={16} strokeWidth={1.5} /></button>
            <button onClick={() => openAddrForm(a)} data-testid={`address-edit-${a.id}`} className="p-2" aria-label="Modifica"><ChevronRight size={20} strokeWidth={1.5} /></button>
          </div>
        ))}
      </div>

      {addrOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="address-form-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setAddrOpen(false)} />
          <form onSubmit={saveAddr} className="relative liquid-glass-panel rounded-t-[2rem] sm:rounded-[2rem] w-full max-w-lg p-6 md:p-8 space-y-3 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-2xl">{editingId ? 'Modifica indirizzo' : 'Nuovo indirizzo'}</h3>
              <button type="button" onClick={() => setAddrOpen(false)} aria-label="Chiudi"><X size={22} strokeWidth={1.5} /></button>
            </div>
            <input required value={addr.nome} onChange={e => setAddr({ ...addr, nome: e.target.value })} placeholder="Nome e cognome" data-testid="addr-nome" className="w-full bg-white border border-essenya-border rounded-xl px-4 py-3 outline-none focus:border-essenya-ink" />
            <input required value={addr.via} onChange={e => setAddr({ ...addr, via: e.target.value })} placeholder="Via e numero civico" data-testid="addr-via" className="w-full bg-white border border-essenya-border rounded-xl px-4 py-3 outline-none focus:border-essenya-ink" />
            <div className="grid grid-cols-2 gap-3">
              <input required value={addr.cap} onChange={e => setAddr({ ...addr, cap: e.target.value })} placeholder="CAP" data-testid="addr-cap" className="bg-white border border-essenya-border rounded-xl px-4 py-3 outline-none focus:border-essenya-ink" />
              <input required value={addr.citta} onChange={e => setAddr({ ...addr, citta: e.target.value })} placeholder="Città" data-testid="addr-citta" className="bg-white border border-essenya-border rounded-xl px-4 py-3 outline-none focus:border-essenya-ink" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input value={addr.provincia} onChange={e => setAddr({ ...addr, provincia: e.target.value })} placeholder="Provincia (es. MI)" data-testid="addr-provincia" className="bg-white border border-essenya-border rounded-xl px-4 py-3 outline-none focus:border-essenya-ink" />
              <input value={addr.paese} onChange={e => setAddr({ ...addr, paese: e.target.value })} placeholder="Paese" data-testid="addr-paese" className="bg-white border border-essenya-border rounded-xl px-4 py-3 outline-none focus:border-essenya-ink" />
            </div>
            <label className="flex items-center gap-2 text-sm text-essenya-text py-1">
              <input type="checkbox" checked={addr.predefinito} onChange={e => setAddr({ ...addr, predefinito: e.target.checked })} data-testid="addr-default" /> Imposta come predefinito
            </label>
            <button type="submit" data-testid="addr-save" className="btn-primary w-full">Salva indirizzo</button>
          </form>
        </div>
      )}

      {/* Orders */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl md:text-3xl">I tuoi ordini</h2>
        <button onClick={() => { logout(); navigate('/'); }} data-testid="logout-button" className="text-sm text-essenya-text underline hover:text-essenya-ink">Esci</button>
      </div>
      {orders.length === 0 ? (
        <p className="text-essenya-text">Non hai ancora effettuato ordini. <Link to="/shop" className="underline">Inizia ora</Link></p>
      ) : (
        <div className="space-y-4">
          {orders.map(o => (
            <div key={o.id} className="card-essenya p-5 flex flex-wrap justify-between gap-4" data-testid={`order-${o.id}`}>
              <div>
                <p className="text-eyebrow">Ordine</p>
                <p className="font-medium">{o.tracking_number}</p>
                <p className="text-essenya-text text-sm">{new Date(o.created_at).toLocaleDateString('it-IT')}</p>
              </div>
              <div className="text-right">
                <p className="font-display text-xl">{(o.total || 0).toFixed(2).replace('.', ',')}€</p>
                <p className="text-essenya-text text-sm">{o.shipping_status}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
